// ============================================================================
// sortable.jsx
// Client-side column sorting for a table whose rows are already on the page
// (no server round trip) — a click cycles asc -> desc -> unsorted, which
// restores whatever order the server originally sent rather than stranding a
// reader in some direction they did not ask for.
// ============================================================================

import { createContext, useContext, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { tbl } from '../../theme';

// A page can give every SortTh beneath it one header colour -- { background,
// color } -- by providing it here, instead of threading a style prop through
// every column of every table. Unset (the default) leaves headers as theme.js
// draws them.
export const HeaderTintContext = createContext(null);

export function useSort(initialKey = null) {
  const [key, setKey] = useState(initialKey);
  const [dir, setDir] = useState('asc');
  const onSort = (k) => {
    if (key !== k) { setKey(k); setDir('asc'); return; }
    if (dir === 'asc') { setDir('desc'); return; }
    setKey(null); setDir('asc');
  };
  return { key, dir, onSort };
}

// getters: { [sortKey]: (row) => string | number } — only the keys a caller
// actually wants sortable need an entry.
export function sortRows(rows, key, dir, getters) {
  if (!key || !getters[key]) return rows;
  const get = getters[key];
  const sorted = [...rows].sort((a, b) => {
    const av = get(a); const bv = get(b);
    if (typeof av === 'string' || typeof bv === 'string') {
      return String(av ?? '').localeCompare(String(bv ?? ''));
    }
    return (av ?? 0) - (bv ?? 0);
  });
  return dir === 'desc' ? sorted.reverse() : sorted;
}

// sortKey={null} (the default) renders a plain, unclickable header — for an
// icon-only column or one with no sensible sort order. `sticky` follows
// theme.js's own documented pattern for a frozen header row (th.js:202):
// each th keeps its own opaque background, so pinning it in place never
// shows the scrolling body bleeding through underneath.
export function SortTh({ label, sortKey: key = null, sort, align = 'left', style: thStyle, sticky = false }) {
  const tint = useContext(HeaderTintContext);
  const defaultStyle = align === 'right' ? tbl.thR : align === 'center' ? tbl.thC : tbl.th;
  const base = { ...(thStyle || defaultStyle), ...tint, ...(sticky ? { position: 'sticky', top: 0, zIndex: 1 } : null) };
  const justify = align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start';
  if (!key || !sort) return <th style={base}>{label}</th>;

  const active = sort.key === key;
  const Icon = !active ? ChevronsUpDown : sort.dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th style={{ ...base, cursor: 'pointer', userSelect: 'none' }} onClick={() => sort.onSort(key)}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                     justifyContent: justify, width: '100%' }}>
        {align === 'right' && <Icon size={12} style={{ opacity: active ? 1 : 0.35, flexShrink: 0 }} />}
        {label}
        {align !== 'right' && <Icon size={12} style={{ opacity: active ? 1 : 0.35, flexShrink: 0 }} />}
      </span>
    </th>
  );
}

// A tall, internally-scrolling table body — for a table that would otherwise
// make the whole page scroll past it. Combine with `sticky` on every SortTh
// in the header row so it stays visible while the body scrolls under it.
export const scrollableTable = (maxHeight = 480) => ({ overflow: 'auto', maxHeight });
